import type {
  FindProductsInput,
  ProductBbox,
} from '@travel-planner/shared/schemas';
import type { CardPace } from '@travel-planner/shared/schemas/card-facts';
import type { AdvisorProductType, AdvisorRegion } from './types';
import type { PlannerServerSort } from './v3-results';
import { ADVISOR_TYPE_CATEGORIES } from './catalog';
import { DEFAULT_ADVISOR_COUNTRY } from './country';

export const ADVISOR_PAGE_SIZE = 8;

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export const isUuid = (value: string): boolean => UUID_RE.test(value);

export const resolveAdvisorRegionId = (
  regionId: string | null,
  regions: AdvisorRegion[]
): string | null =>
  regionId &&
  isUuid(regionId) &&
  (regions.length === 0 || regions.some((region) => region.id === regionId))
    ? regionId
    : null;

export const resolveAdvisorRegionIds = (
  regionIds: readonly string[],
  regions: AdvisorRegion[]
): string[] => [
  ...new Set(
    regionIds.filter((id) => resolveAdvisorRegionId(id, regions) !== null)
  ),
];

const baseFilters = (
  type: AdvisorProductType,
  country: string,
  regionIds: readonly string[]
) => ({
  contentCategory: [...ADVISOR_TYPE_CATEGORIES[type]],
  country: [country || DEFAULT_ADVISOR_COUNTRY],
  customRegionIds: regionIds.length > 0 ? [...regionIds] : undefined,
});

// Consultant catalogue: not hidden, not hidden from the Mini Planner, no CMS
// storefront gate. Type tags filter via categoryHashtags. Moods only
// pick regions / autoselect those tags — they are not sent here.
const pageOffset = (page: number): number =>
  (Math.max(1, page) - 1) * ADVISOR_PAGE_SIZE;

export type AdvisorListExtras = {
  search?: string;
  bbox?: ProductBbox | null;
  countOnly?: boolean;
  ids?: readonly string[];
  preferPace?: CardPace;
  /** The whole catalogue in this order (price, most booked). */
  sort?: PlannerServerSort | null;
  hotelStars?: readonly number[];
  hotelPriceLevels?: readonly number[];
};

export const toAdvisorListInput = (
  type: AdvisorProductType,
  country: string,
  regionIds: readonly string[] = [],
  page = 1,
  tags: readonly string[] = [],
  extras: AdvisorListExtras = {}
): FindProductsInput => {
  // An id lookup keeps its own query even when empty, so a cached catalogue
  // page is never shown as its placeholder.
  if (extras.ids) {
    return {
      offset: 0,
      limit: Math.max(1, extras.ids.length),
      isHidden: false,
      isHiddenOnMiniPlanner: false,
      includeSubItems: false,
      country: [country || DEFAULT_ADVISOR_COUNTRY],
      ids: [...extras.ids],
    };
  }
  const search = extras.search?.trim();
  return {
    offset: extras.countOnly ? 0 : pageOffset(page),
    limit: extras.countOnly ? 1 : ADVISOR_PAGE_SIZE,
    isHidden: false,
    isHiddenOnMiniPlanner: false,
    includeSubItems: false,
    ...baseFilters(type, country, regionIds),
    ...(tags.length > 0 ? { categoryHashtags: [...tags] } : {}),
    ...(search ? { search } : {}),
    ...(extras.preferPace ? { preferPace: extras.preferPace } : {}),
    ...(extras.sort && !extras.countOnly ? { ...extras.sort } : {}),
    ...(type === 'hotels' && extras.hotelStars?.length
      ? { hotelStars: [...extras.hotelStars] }
      : {}),
    ...(type === 'hotels' && extras.hotelPriceLevels?.length
      ? { hotelPriceLevels: [...extras.hotelPriceLevels] }
      : {}),
    // "Only what is on the map" narrows within the picked regions.
    ...(extras.bbox
      ? {
          bbox: { ...extras.bbox },
          ...(regionIds.length > 0 ? { bboxWithinRegion: true } : {}),
        }
      : {}),
  };
};

// The request's pace as experiences' card details spell it.
const PACE_OF_BRIEF: Record<'relaxed' | 'balanced' | 'active', CardPace> = {
  relaxed: 'Relaxed',
  balanced: 'Balanced',
  active: 'Active',
};

/** The card pace to list first for a brief; none for hotels and restaurants. */
export const preferredPace = (
  type: AdvisorProductType,
  briefPace: 'relaxed' | 'balanced' | 'active' | null | undefined
): CardPace | undefined =>
  type === 'experiences' && briefPace ? PACE_OF_BRIEF[briefPace] : undefined;
